import React, { useMemo } from 'react'
import clsx from 'clsx'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { useAPISyncToEkin } from 'api/kinerja/pengawas-sekolah/landing-page'
import { useAuth } from 'utils/auth'
import useToggleSdkContext from 'utils/useToogleSdk'

import type {
  StatusPerencanaan,
  StatusEkinSync,
} from 'types/kinerja-ps-pegawai'

type TSyncToEkinProps = {
  perencanaanStatus: StatusPerencanaan
  ekinSyncErrorMessage?: string
  ekinSyncStatus?: StatusEkinSync
  isReadOnly?: boolean
  isError?: boolean
}

const isTestEmail = (email: string): boolean => {
  // Regex to match email that contains 'dinas-test' and a number between 1-80, eg. 'dinas-test1', 'dinas-test80'
  const regex = /^dinas-test[1-80]/
  return regex.test(email)
}

export const SyncToEkin: React.FC<TSyncToEkinProps> = ({
  perencanaanStatus,
  ekinSyncErrorMessage = '',
  ekinSyncStatus = 'INIT',
  isReadOnly = false,
  isError = false,
}) => {
  const toggle = useToggleSdkContext()
  const pelaksanaanAutoStartEnabled =
    toggle?.['kinerja-ps-pegawai-pelaksanaan-auto-start-enabled'] || false

  const isPelaksanaanState = ['DISETUJUI_PPK', 'PELAKSANAAN_DIMULAI'].includes(
    perencanaanStatus
  )

  // E-Kinerja Sync
  const isEkinSyncSuccess = ['RHK_CREATED', 'PERSETUJUAN_SUCCESS'].includes(
    ekinSyncStatus
  )
  const isEkinSyncFailed = [
    'RHK_FAILED',
    'SKP_FAILED',
    'PERSETUJUAN_FAILED',
  ].includes(ekinSyncStatus)
  const isEkinSynced = isEkinSyncSuccess || isEkinSyncFailed

  // Sync Button
  const showSyncButton = React.useMemo(() => {
    const autoStartFailed = pelaksanaanAutoStartEnabled && isEkinSyncFailed
    const manualStartActive = !pelaksanaanAutoStartEnabled && isPelaksanaanState

    return autoStartFailed || manualStartActive
  }, [isEkinSyncFailed, isPelaksanaanState, pelaksanaanAutoStartEnabled])

  const { isLoading, mutateAsync } = useAPISyncToEkin()
  const { period } = usePeriodContext()
  const toast = useToast()

  const { session } = useAuth()

  const isTestAccount = useMemo(() => {
    const { email = '', loginEmail = '' } = session?.user || {}
    return isTestEmail(email) || isTestEmail(loginEmail)
  }, [session])

  const submit = async () => {
    try {
      if (isTestAccount) {
        toast({
          message: 'Tidak bisa kirim ke E-Kinerja untuk akun testing',
        })
      } else {
        await mutateAsync({ periodeId: period.id })
        toast({ message: 'Berhasil kirim data ke E-Kinerja!', type: 'success' })
      }
    } catch (e) {
      toast({ message: 'Gagal kirim data ke E-Kinerja. Coba lagi.' })
    }
  }

  return (
    <SimpleCard
      className={clsx(
        'p-4 space-y-2 border w-96',
        isError && '!bg-surface-neutral-default !border-default',
        !isError &&
          !isEkinSynced &&
          '!bg-surface-informational-subdued !border-blue-40',
        isEkinSyncSuccess && '!border-green-50 !bg-surface-success-subdued',
        !isError &&
          isEkinSyncFailed &&
          '!border-red-40 !bg-surface-critical-subdued'
      )}
    >
      <div className="flex space-x-2">
        <div className="max-w-64">
          <Text variant="heading-sm">
            Kirim data Perencanaan (SKP) ke E-Kinerja
          </Text>
        </div>

        {isPelaksanaanState && (
          <div className="text-right">
            {isEkinSyncSuccess && (
              <div className="w-24">
                <Icon data-testid="sync-to-ekin-success-icon" color="success">
                  check_circle_outline
                </Icon>
              </div>
            )}

            {showSyncButton && (
              <Button
                data-testid="sync-to-ekin-button"
                disabled={isLoading}
                isLoading={isLoading}
                className="flex-1 !py-2 space-x-1"
                size="sm"
                {...(!isReadOnly && { onClick: submit })}
              >
                <Icon color="inverse" fontSize="sm">
                  share
                </Icon>
                <span>Kirim</span>
              </Button>
            )}
          </div>
        )}
      </div>

      {isError && <Badge>Terjadi kendala sistem. Mohon coba lagi nanti.</Badge>}

      {!isError && (
        <>
          {isEkinSyncSuccess && (
            <Badge variant="success">
              Berhasil dan sudah tampil di E-Kinerja
            </Badge>
          )}

          {isEkinSyncFailed && (
            <>
              <Badge variant="critical">Gagal</Badge>
              {Boolean(ekinSyncErrorMessage) && (
                <Text variant="body-sm">Catatan: {ekinSyncErrorMessage}</Text>
              )}
            </>
          )}

          {!isEkinSynced && (
            <>
              {isPelaksanaanState ? (
                <Badge variant="informational">
                  {pelaksanaanAutoStartEnabled
                    ? 'Sedang dikirim'
                    : 'Siap dikirim'}
                </Badge>
              ) : (
                <Badge variant="neutral">
                  Selesaikan Perencanaan terlebih dahulu
                </Badge>
              )}
            </>
          )}
        </>
      )}
    </SimpleCard>
  )
}
